import { useEffect, useRef, useState, useCallback } from "react";

export type IdleState = "active" | "warning" | "locked";

interface UseIdleTimerOptions {
  enabled: boolean;
  idleMs: number;
  warningMs: number;
}

interface UseIdleTimerResult {
  state: IdleState;
  reset: () => void;
  lock: () => void;
}

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
] as const;

const ACTIVITY_THROTTLE_MS = 1000;

export function useIdleTimer({ enabled, idleMs, warningMs }: UseIdleTimerOptions): UseIdleTimerResult {
  const [state, setState] = useState<IdleState>("active");
  const stateRef = useRef<IdleState>("active");
  const lastActivityRef = useRef<number>(Date.now());
  const lastThrottleRef = useRef<number>(0);
  const remainingOnHideRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const idleMsRef = useRef(idleMs);
  const warningMsRef = useRef(warningMs);
  const enabledRef = useRef(enabled);

  useEffect(() => { idleMsRef.current = idleMs; }, [idleMs]);
  useEffect(() => { warningMsRef.current = warningMs; }, [warningMs]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const setStateBoth = useCallback((next: IdleState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Single tick function held in a ref so it can refer to scheduleTick freely.
  const tickFnRef = useRef<() => void>(() => {});
  const scheduleTickRef = useRef<(delay: number) => void>(() => {});

  scheduleTickRef.current = (delay: number) => {
    clearTick();
    tickRef.current = window.setTimeout(() => tickFnRef.current(), Math.max(50, delay));
  };

  tickFnRef.current = () => {
    tickRef.current = null;
    if (!enabledRef.current) return;
    if (document.hidden) {
      const elapsed = Date.now() - lastActivityRef.current;
      remainingOnHideRef.current = Math.max(0, idleMsRef.current - elapsed);
      return;
    }
    if (stateRef.current === "locked") return;

    const elapsed = Date.now() - lastActivityRef.current;
    if (elapsed >= idleMsRef.current) {
      setStateBoth("locked");
      return;
    }
    if (elapsed >= warningMsRef.current) {
      if (stateRef.current !== "warning") {
        setStateBoth("warning");
      }
      scheduleTickRef.current(idleMsRef.current - elapsed);
      return;
    }
    if (stateRef.current !== "active") {
      setStateBoth("active");
    }
    scheduleTickRef.current(warningMsRef.current - elapsed);
  };

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    lastThrottleRef.current = Date.now();
    remainingOnHideRef.current = null;
    if (stateRef.current !== "active") {
      setStateBoth("active");
    }
    if (enabledRef.current && !document.hidden) {
      scheduleTickRef.current(warningMsRef.current);
    }
  }, [setStateBoth]);

  const lock = useCallback(() => {
    clearTick();
    setStateBoth("locked");
  }, [clearTick, setStateBoth]);

  useEffect(() => {
    if (!enabled) {
      clearTick();
      stateRef.current = "active";
      setState("active");
      return;
    }

    const onActivity = () => {
      if (stateRef.current === "locked") return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastThrottleRef.current = now;
      if (stateRef.current === "warning") {
        // Don't auto-clear warning on movement — user must click "Stay signed in".
        return;
      }
      if (!document.hidden) {
        scheduleTickRef.current(warningMsRef.current);
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        const elapsed = Date.now() - lastActivityRef.current;
        remainingOnHideRef.current = Math.max(0, idleMsRef.current - elapsed);
        clearTick();
      } else {
        if (stateRef.current === "locked") return;
        const remaining = remainingOnHideRef.current;
        remainingOnHideRef.current = null;
        if (remaining !== null) {
          lastActivityRef.current = Date.now() - (idleMsRef.current - remaining);
        }
        tickFnRef.current();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, onActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibility);

    lastActivityRef.current = Date.now();
    lastThrottleRef.current = Date.now();
    if (!document.hidden) {
      scheduleTickRef.current(warningMs);
    } else {
      remainingOnHideRef.current = idleMs;
    }

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, onActivity);
      });
      document.removeEventListener("visibilitychange", onVisibility);
      clearTick();
    };
  }, [enabled, idleMs, warningMs, clearTick]);

  return { state, reset, lock };
}
